import React, { useState, useEffect, useContext, useMemo } from "react";
import toast from "react-hot-toast";
import { Eye, Search, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import { AuthContext } from "../../context/Auth";
import { createAdminUser } from "../../business/AdminBusiness";
import { deleteUser, getAllUsers } from "../../business/UserBusiness";
import {
  Avatar, Badge, Button, Card, ConfirmModal, EmptyState, Field, IconButton, Modal, Page, PageHeader,
  SkeletonRows, SortHeader, inputClass,
} from "../../components/ui";

// A conta do Alexandre só pode ser excluída por ele mesmo
const OWNER_EMAIL = 'alexandresaintss@gmail.com';
const EMPTY_ADMIN = { name: '', email: '', phone: '', password: '', repeatPassword: '' };

const formatDate = (value) => new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });

// Colaboradores = contas com papel de administrador
export const Collaborator = () => {
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_ADMIN);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deletingPerson, setDeletingPerson] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const { user } = useContext(AuthContext);
  const isLocked = (person) => person?.email === OWNER_EMAIL && user?.email !== OWNER_EMAIL;

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      const response = await getAllUsers();
      setAdmins((response?.data || []).filter((u) => u?.role === 'admin'));
    } catch (err) {
      toast.error('Erro ao carregar os administradores');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const sortedAdmins = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const list = admins.filter((a) => a?.username?.toLowerCase().includes(term) || a?.email?.toLowerCase().includes(term));
    return [...list].sort((a, b) => {
      if (sortBy === 'username') {
        const [x, y] = [a.username?.toLowerCase() || '', b.username?.toLowerCase() || ''];
        return sortOrder === 'asc' ? x.localeCompare(y) : y.localeCompare(x);
      }
      const [x, y] = [new Date(a.createdAt || 0), new Date(b.createdAt || 0)];
      return sortOrder === 'asc' ? x - y : y - x;
    });
  }, [admins, searchTerm, sortBy, sortOrder]);

  const handleSortChange = (column) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('desc'); }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleAddAdmin = async () => {
    const { name, email, phone, password, repeatPassword } = form;
    if (!name || !email || !phone || !password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (password !== repeatPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    try {
      setSaving(true);
      await createAdminUser({ username: name, email, phone, password, role: 'admin' });
      toast.success('Administrador criado!');
      setShowAddModal(false);
      setForm(EMPTY_ADMIN);
      fetchAdmins();
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Erro ao criar administrador');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setSaving(true);
      await deleteUser(deletingPerson._id);
      toast.success('Administrador removido');
      setDeletingPerson(null);
      setSelectedPerson(null);
      fetchAdmins();
    } catch (error) {
      toast.error('Erro ao excluir o administrador');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Colaboradores" description="Quem tem acesso a este painel.">
        <Button icon={UserPlus} onClick={() => setShowAddModal(true)}>Adicionar administrador</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-neutral-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 dark:border-white/[0.05]">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nome ou e-mail..." className={`${inputClass} pl-9 pr-8`} />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600" aria-label="Limpar busca"><X className="size-4" /></button>
            )}
          </div>
          <p className="text-sm text-neutral-500">{admins.length} administrador{admins.length === 1 ? '' : 'es'}</p>
        </div>

        {isLoading ? (
          <SkeletonRows rows={4} className="px-5" />
        ) : sortedAdmins.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="Nenhum administrador encontrado" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-neutral-100 dark:border-white/[0.05]">
                <tr>
                  <SortHeader label="Administrador" active={sortBy === 'username'} order={sortOrder} onClick={() => handleSortChange('username')} className="pl-5" />
                  <th className="hidden px-4 py-3 text-left text-xs font-medium text-neutral-500 md:table-cell dark:text-neutral-400">Telefone</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-medium text-neutral-500 sm:table-cell dark:text-neutral-400">Assinatura</th>
                  <SortHeader label="Criado em" active={sortBy === 'createdAt'} order={sortOrder} onClick={() => handleSortChange('createdAt')} className="hidden lg:table-cell" />
                  <th className="w-px px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
                {sortedAdmins.map((person) => (
                  <tr key={person._id || person.email} onClick={() => setSelectedPerson(person)} className="cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-white/[0.02]">
                    <td className="py-3 pl-5 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={person.username} />
                        <div className="min-w-0">
                          <p className="max-w-[200px] truncate text-sm text-neutral-900 dark:text-neutral-100">{person.username}</p>
                          <p className="max-w-[240px] truncate text-xs text-neutral-500">{person.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-600 md:table-cell dark:text-neutral-300">{person.phone}</td>
                    <td className="hidden whitespace-nowrap px-4 sm:table-cell">
                      <Badge tone={person.subscription === 'active' ? 'green' : 'neutral'}>{person.subscription === 'active' ? 'Ativa' : 'Inativa'}</Badge>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 text-sm text-neutral-500 lg:table-cell">{person.createdAt ? formatDate(person.createdAt) : '—'}</td>
                    <td className="px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <IconButton icon={Eye} label="Ver detalhes" onClick={() => setSelectedPerson(person)} />
                        <IconButton icon={Trash2} tone="danger" label={isLocked(person) ? 'Só o próprio Alexandre pode excluir' : 'Excluir'} disabled={isLocked(person)} onClick={() => setDeletingPerson(person)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showAddModal && (
        <Modal
          title="Adicionar administrador"
          onClose={() => setShowAddModal(false)}
          footer={<>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleAddAdmin} loading={saving}>Criar administrador</Button>
          </>}
        >
          <div className="flex flex-col gap-3">
            <Field label="Nome"><input className={inputClass} placeholder="Nome completo" value={form.name} onChange={(e) => setField('name', e.target.value)} /></Field>
            <Field label="E-mail"><input type="email" className={inputClass} placeholder="email@exemplo.com" value={form.email} onChange={(e) => setField('email', e.target.value)} /></Field>
            <Field label="Telefone" hint="Com o código do país na frente (55...)"><input type="tel" className={inputClass} placeholder="5511999999999" value={form.phone} onChange={(e) => setField('phone', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Senha"><input type="password" className={inputClass} value={form.password} onChange={(e) => setField('password', e.target.value)} /></Field>
              <Field label="Repetir senha"><input type="password" className={inputClass} value={form.repeatPassword} onChange={(e) => setField('repeatPassword', e.target.value)} /></Field>
            </div>
          </div>
        </Modal>
      )}

      {selectedPerson && (
        <Modal
          title="Detalhes do administrador"
          onClose={() => setSelectedPerson(null)}
          footer={!isLocked(selectedPerson) && (
            <Button variant="secondary" icon={Trash2} className="text-red-500" onClick={() => setDeletingPerson(selectedPerson)}>Excluir</Button>
          )}
        >
          <div className="flex items-center gap-3">
            <Avatar name={selectedPerson.username} size="size-14" />
            <div className="min-w-0">
              <p className="truncate text-base font-medium">{selectedPerson.username}</p>
              <p className="truncate text-sm text-neutral-500">{selectedPerson.email}</p>
            </div>
          </div>
          <dl className="mt-5 divide-y divide-neutral-100 rounded-2xl bg-neutral-50 px-4 text-sm dark:divide-white/[0.05] dark:bg-white/[0.03]">
            {[
              ['Telefone', selectedPerson.phone],
              ['Papel', <Badge tone="red">Administrador</Badge>],
              ['Registros', selectedPerson.transactions?.length || 0],
              ['Criado em', selectedPerson.createdAt ? formatDate(selectedPerson.createdAt) : '—'],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4 py-2.5">
                <dt className="text-neutral-500">{label}</dt>
                <dd className="text-right text-neutral-900 dark:text-neutral-100">{value}</dd>
              </div>
            ))}
          </dl>
        </Modal>
      )}

      {deletingPerson && (
        <ConfirmModal title="Excluir administrador" confirmLabel="Excluir" loading={saving} onConfirm={confirmDelete} onClose={() => setDeletingPerson(null)}>
          A conta de <b className="text-neutral-900 dark:text-neutral-100">{deletingPerson.username}</b> ({deletingPerson.email}) vai ser apagada e perde o acesso ao painel. Isso não pode ser desfeito.
        </ConfirmModal>
      )}
    </Page>
  );
};
